/**
 * EpubCFI — parse, compare, and resolve EPUB Canonical Fragment Identifiers.
 *
 * Implements character-offset and simple-range CFIs per the IDPF spec:
 *   http://www.idpf.org/epub/linking/cfi/epub-cfi.html
 *
 * Drop-in replacement for epub.js EpubCFI class.
 */

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;
const DOCUMENT_NODE = 9;

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface Step {
  type: "element" | "text";
  index: number;
  id: string | null;
}

interface Terminal {
  offset: number | null;
  assertion: string | null;
}

interface Component {
  steps: Step[];
  terminal: Terminal;
}

export interface CfiData {
  spinePos: number;
  range: boolean;
  base: Component;
  path: Component;
  start: Component | null;
  end: Component | null;
}

function emptyTerminal(): Terminal {
  return { offset: null, assertion: null };
}

function emptyComponent(): Component {
  return { steps: [], terminal: emptyTerminal() };
}

// ---------------------------------------------------------------------------
// Parsing helpers (pure functions)
// ---------------------------------------------------------------------------

function parseStep(stepStr: string): Step | undefined {
  const num = parseInt(stepStr, 10);
  if (isNaN(num)) return undefined;

  const bracketMatch = stepStr.match(/\[(.*)]/);
  const id = bracketMatch?.[1] ?? null;

  if (num % 2 === 0) {
    return { type: "element", index: num / 2 - 1, id };
  } else {
    return { type: "text", index: (num - 1) / 2, id };
  }
}

function parseTerminal(termStr: string): Terminal {
  const assertion = termStr.match(/\[(.*)]/);
  let characterOffset: number | null;
  let textAssertion: string | null = null;

  if (assertion?.[1]) {
    characterOffset = parseInt(termStr.split("[")[0], 10);
    textAssertion = assertion[1];
  } else {
    characterOffset = parseInt(termStr, 10);
  }

  if (isNaN(characterOffset as number)) characterOffset = null;
  return { offset: characterOffset, assertion: textAssertion };
}

function parseComponent(componentStr: string): Component {
  const parts = componentStr.split(":");
  const stepParts = parts[0].split("/");
  if (stepParts[0] === "") stepParts.shift();

  const steps: Step[] = [];
  for (const s of stepParts) {
    const step = parseStep(s);
    if (step) steps.push(step);
  }

  return {
    steps,
    terminal: parts.length > 1 ? parseTerminal(parts[1]) : emptyTerminal(),
  };
}

// ---------------------------------------------------------------------------
// Serialisation helpers
// ---------------------------------------------------------------------------

function joinSteps(steps: Step[]): string {
  return steps
    .map((step) => {
      let seg: string;
      if (step.type === "element") {
        seg = String((step.index + 1) * 2);
      } else {
        seg = String(1 + 2 * step.index);
      }
      if (step.id) seg += `[${step.id}]`;
      return seg;
    })
    .join("/");
}

function segmentString(segment: Component): string {
  let s = "/" + joinSteps(segment.steps);
  if (segment.terminal?.offset != null) {
    s += ":" + segment.terminal.offset;
  }
  if (segment.terminal?.assertion != null) {
    s += "[" + segment.terminal.assertion + "]";
  }
  return s;
}

// ---------------------------------------------------------------------------
// DOM traversal helpers
// ---------------------------------------------------------------------------

function findChildren(node: Node): Element[] {
  const children: Element[] = [];
  let child = node.firstChild;
  while (child) {
    if (child.nodeType === ELEMENT_NODE) children.push(child as Element);
    child = child.nextSibling;
  }
  return children;
}

function textNodes(container: Node, ignoreClass?: string): (Text | Element)[] {
  return Array.from(container.childNodes).filter((node) => {
    if (node.nodeType === TEXT_NODE) return true;
    if (
      ignoreClass &&
      node.nodeType === ELEMENT_NODE &&
      (node as Element).classList.contains(ignoreClass)
    ) {
      // Zero-length inline markers should be excluded
      if (
        (node as Element).getAttribute("data-inline-note-marker") === "true" ||
        (node as Element).getAttribute("data-reaction-spacer") === "true"
      ) {
        return false;
      }
      return true; // Treated as text for indexing purposes
    }
    return false;
  }) as (Text | Element)[];
}

function isZeroLengthIgnoredNode(
  node: Node | null,
  ignoreClass?: string,
): boolean {
  return !!(
    ignoreClass &&
    node &&
    node.nodeType === ELEMENT_NODE &&
    (node as Element).classList?.contains(ignoreClass) &&
    ((node as Element).getAttribute?.("data-inline-note-marker") === "true" ||
      (node as Element).getAttribute?.("data-reaction-spacer") === "true")
  );
}

function ignoredNodeTextLength(
  node: Node | null,
  ignoreClass?: string,
): number {
  if (!node) return 0;
  if (isZeroLengthIgnoredNode(node, ignoreClass)) return 0;
  return node.textContent?.length ?? 0;
}

/**
 * Build a map from child index → normalised CFI index, merging adjacent
 * text/ignored nodes.
 */
function normalizedMap(
  children: NodeListOf<ChildNode> | Element[],
  nodeType: number,
  ignoreClass?: string,
): Record<number, number> {
  const output: Record<number, number> = {};

  if (nodeType === TEXT_NODE) {
    let precedingElementSlots = 0;

    for (let i = 0; i < children.length; i++) {
      let currNodeType = children[i].nodeType;

      if (isZeroLengthIgnoredNode(children[i], ignoreClass)) continue;

      if (
        currNodeType === ELEMENT_NODE &&
        ignoreClass &&
        (children[i] as Element).classList.contains(ignoreClass)
      ) {
        currNodeType = TEXT_NODE; // treat ignored elements as text
      }

      if (currNodeType === TEXT_NODE) {
        output[i] = precedingElementSlots;
      } else if (currNodeType === ELEMENT_NODE) {
        precedingElementSlots++;
      }
    }

    return output;
  }

  let prevIndex = -1;
  let prevNodeType: number | undefined;

  for (let i = 0; i < children.length; i++) {
    let currNodeType = children[i].nodeType;

    if (isZeroLengthIgnoredNode(children[i], ignoreClass)) continue;

    if (
      currNodeType === ELEMENT_NODE &&
      ignoreClass &&
      (children[i] as Element).classList.contains(ignoreClass)
    ) {
      currNodeType = TEXT_NODE; // treat ignored elements as text
    }

    if (i > 0 && currNodeType === TEXT_NODE && prevNodeType === TEXT_NODE) {
      output[i] = prevIndex; // merge with previous
    } else if (nodeType === currNodeType) {
      prevIndex++;
      output[i] = prevIndex;
    }

    prevNodeType = currNodeType;
  }

  return output;
}

// ---------------------------------------------------------------------------
// The EpubCFI class
// ---------------------------------------------------------------------------

class EpubCFI {
  str: string = "";
  base: Component;
  spinePos: number = 0;
  range: boolean = false;
  path: Component;
  start: Component | null = null;
  end: Component | null = null;

  constructor(
    cfiFrom?: string | Range | Node | EpubCFI,
    base?: string | Component,
    ignoreClass: string = "epubjs-ignore",
  ) {
    this.base = emptyComponent();
    this.path = emptyComponent();

    // Allow construction without `new`
    if (!(this instanceof EpubCFI)) {
      return new EpubCFI(cfiFrom, base, ignoreClass);
    }

    if (typeof base === "string") {
      this.base = parseComponent(base);
    } else if (base && typeof base === "object" && (base as Component).steps) {
      this.base = base as Component;
    }

    const inputType = this.checkType(cfiFrom);

    if (inputType === "string") {
      this.str = cfiFrom as string;
      Object.assign(this, this.parse(cfiFrom as string));
    } else if (inputType === "range") {
      Object.assign(
        this,
        this.fromRange(cfiFrom as Range, this.base, ignoreClass),
      );
    } else if (inputType === "node") {
      Object.assign(
        this,
        this.fromNode(cfiFrom as Node, this.base, ignoreClass),
      );
    } else if (inputType === "EpubCFI") {
      return cfiFrom as EpubCFI;
    }
  }

  // ---- Type detection ----

  private checkType(
    cfi: unknown,
  ): "string" | "range" | "node" | "EpubCFI" | false {
    if (this.isCfiString(cfi as string)) return "string";
    if (
      cfi &&
      typeof cfi === "object" &&
      typeof (cfi as Range).startContainer !== "undefined"
    )
      return "range";
    if (
      cfi &&
      typeof cfi === "object" &&
      typeof (cfi as Node).nodeType !== "undefined"
    )
      return "node";
    if (cfi instanceof EpubCFI) return "EpubCFI";
    return false;
  }

  // ---- Parsing ----

  parse(cfiStr: string): CfiData {
    if (typeof cfiStr !== "string")
      return {
        spinePos: -1,
        range: false,
        base: emptyComponent(),
        path: emptyComponent(),
        start: null,
        end: null,
      };

    // Strip epubcfi() wrapper
    if (cfiStr.startsWith("epubcfi(") && cfiStr.endsWith(")")) {
      cfiStr = cfiStr.slice(8, -1);
    }

    const indirection = cfiStr.split("!");
    const baseComponent = indirection[0];
    if (!baseComponent)
      return {
        spinePos: -1,
        range: false,
        base: emptyComponent(),
        path: emptyComponent(),
        start: null,
        end: null,
      };

    const base = parseComponent(baseComponent);

    let pathStr = "";
    let rangeStrs: [string, string] | null = null;

    if (indirection[1]) {
      const ranges = indirection[1].split(",");
      pathStr = ranges[0];
      if (ranges.length === 3) {
        rangeStrs = [ranges[1], ranges[2]];
      }
    }

    const path = pathStr ? parseComponent(pathStr) : emptyComponent();
    let start: Component | null = null;
    let end: Component | null = null;
    let isRange = false;

    if (rangeStrs) {
      isRange = true;
      start = parseComponent(rangeStrs[0]);
      end = parseComponent(rangeStrs[1]);
    }

    const spinePos = base.steps[1]?.index ?? -1;

    return { spinePos, range: isRange, base, path, start, end };
  }

  // ---- Serialisation ----

  toString(): string {
    let s =
      "epubcfi(" + segmentString(this.base) + "!" + segmentString(this.path);
    if (this.range && this.start) s += "," + segmentString(this.start);
    if (this.range && this.end) s += "," + segmentString(this.end);
    s += ")";
    return s;
  }

  // ---- Comparison ----

  compare(cfiOne: string | CfiData, cfiTwo: string | CfiData): -1 | 0 | 1 {
    const a: CfiData =
      typeof cfiOne === "string" ? new EpubCFI(cfiOne) : cfiOne;
    const b: CfiData =
      typeof cfiTwo === "string" ? new EpubCFI(cfiTwo) : cfiTwo;

    if (a.spinePos > b.spinePos) return 1;
    if (a.spinePos < b.spinePos) return -1;

    const stepsA = a.range ? a.path.steps.concat(a.start!.steps) : a.path.steps;
    const terminalA = a.range ? a.start!.terminal : a.path.terminal;

    const stepsB = b.range ? b.path.steps.concat(b.start!.steps) : b.path.steps;
    const terminalB = b.range ? b.start!.terminal : b.path.terminal;

    for (let i = 0; i < stepsA.length; i++) {
      if (!stepsA[i]) return -1;
      if (!stepsB[i]) return 1;
      if (stepsA[i].index > stepsB[i].index) return 1;
      if (stepsA[i].index < stepsB[i].index) return -1;
    }

    if (stepsA.length < stepsB.length) return -1;

    if ((terminalA.offset ?? 0) > (terminalB.offset ?? 0)) return 1;
    if ((terminalA.offset ?? 0) < (terminalB.offset ?? 0)) return -1;

    return 0;
  }

  // ---- Validation ----

  isCfiString(str: unknown): boolean {
    return (
      typeof str === "string" && str.startsWith("epubcfi(") && str.endsWith(")")
    );
  }

  // ---- Chapter component generation ----

  generateChapterComponent(
    spineNodeIndex: number,
    pos: number,
    id?: string,
  ): string {
    const spineStep = (spineNodeIndex + 1) * 2;
    const itemStep = (pos + 1) * 2;
    let cfi = `/${spineStep}/${itemStep}`;
    if (id) cfi += `[${id}]`;
    return cfi;
  }

  // ---- Collapse range to point ----

  collapse(toStart?: boolean): void {
    if (!this.range) return;
    this.range = false;
    if (toStart) {
      this.path.steps = this.path.steps.concat(this.start!.steps);
      this.path.terminal = this.start!.terminal;
    } else {
      this.path.steps = this.path.steps.concat(this.end!.steps);
      this.path.terminal = this.end!.terminal;
    }
    this.start = null;
    this.end = null;
  }

  // ---- DOM → CFI ----

  fromRange(
    range: Range,
    base: string | Component,
    ignoreClass?: string,
  ): CfiData {
    const cfi: CfiData = {
      range: false,
      base: emptyComponent(),
      path: emptyComponent(),
      start: null,
      end: null,
      spinePos: 0,
    };

    if (typeof base === "string") {
      cfi.base = parseComponent(base);
      cfi.spinePos = cfi.base.steps[1]?.index ?? 0;
    } else if (base && (base as Component).steps) {
      cfi.base = base as Component;
      cfi.spinePos = cfi.base.steps[1]?.index ?? 0;
    }

    const start = range.startContainer;
    const end = range.endContainer;
    let startOffset = range.startOffset;
    let endOffset = range.endOffset;

    if (range.collapsed) {
      cfi.path = this.pathTo(start, startOffset, ignoreClass);
    } else {
      cfi.range = true;

      cfi.start = this.pathTo(start, startOffset, ignoreClass);

      cfi.end = this.pathTo(end, endOffset, ignoreClass);

      // Factor out common prefix into cfi.path
      cfi.path = { steps: [], terminal: emptyTerminal() };
      const len = cfi.start!.steps.length;
      let i: number;
      for (i = 0; i < len; i++) {
        if (this.equalStep(cfi.start!.steps[i], cfi.end!.steps[i])) {
          if (i === len - 1) {
            if (
              cfi.start!.terminal.offset === cfi.end!.terminal.offset &&
              cfi.start!.terminal.assertion === cfi.end!.terminal.assertion
            ) {
              cfi.path.steps.push(cfi.start!.steps[i]);
              cfi.range = false;
            }
          } else {
            cfi.path.steps.push(cfi.start!.steps[i]);
          }
        } else {
          break;
        }
      }

      cfi.start!.steps = cfi.start!.steps.slice(cfi.path.steps.length);
      cfi.end!.steps = cfi.end!.steps.slice(cfi.path.steps.length);
    }

    return cfi;
  }

  fromNode(
    anchor: Node,
    base: string | Component,
    ignoreClass?: string,
  ): CfiData {
    const cfi: CfiData = {
      range: false,
      base: emptyComponent(),
      path: emptyComponent(),
      start: null,
      end: null,
      spinePos: 0,
    };

    if (typeof base === "string") {
      cfi.base = parseComponent(base);
      cfi.spinePos = cfi.base.steps[1]?.index ?? 0;
    } else if (base && (base as Component).steps) {
      cfi.base = base as Component;
      cfi.spinePos = cfi.base.steps[1]?.index ?? 0;
    }

    cfi.path = this.pathTo(anchor, null, ignoreClass);
    return cfi;
  }

  // ---- CFI → DOM ----

  toRange(doc?: Document, ignoreClass?: string): Range | null {
    const _doc = doc || document;
    const range = _doc.createRange();
    const needsIgnoring = ignoreClass
      ? _doc.querySelector("." + ignoreClass) != null
      : false;
    const ig = needsIgnoring ? ignoreClass : undefined;

    let startSteps: Step[];
    let startTerminal: Terminal;
    let endSteps: Step[] | null = null;
    let endTerminal: Terminal | null = null;

    if (this.range && this.start && this.end) {
      startSteps = this.path.steps.concat(this.start.steps);
      startTerminal = this.start.terminal;
      endSteps = this.path.steps.concat(this.end.steps);
      endTerminal = this.end.terminal;
    } else {
      startSteps = this.path.steps;
      startTerminal = this.path.terminal;
    }

    let startContainer = this.findNode(startSteps, _doc, ig);
    if (!startContainer) {
      console.log("No startContainer found for", this.toString());
      return null;
    }

    try {
      range.setStart(startContainer, startTerminal.offset ?? 0);
    } catch {
      const missed = this.fixMiss(
        startSteps,
        startTerminal.offset ?? 0,
        _doc,
        ig,
      );
      range.setStart(missed.container, missed.offset);
    }

    if (endSteps && endTerminal) {
      const endContainer = this.findNode(endSteps, _doc, ig);
      if (endContainer) {
        try {
          range.setEnd(endContainer, endTerminal.offset ?? 0);
        } catch {
          const missed = this.fixMiss(
            endSteps,
            endTerminal.offset ?? 0,
            _doc,
            ig,
          );
          range.setEnd(missed.container, missed.offset);
        }
      }
    }

    return range;
  }

  // ---- Internal DOM helpers ----

  private pathTo(
    node: Node,
    offset: number | null,
    ignoreClass?: string,
  ): Component {
    const segment: Component = {
      steps: [],
      terminal: emptyTerminal(),
    };

    let currentNode: Node | null = node;
    while (
      currentNode &&
      currentNode.parentNode &&
      currentNode.parentNode.nodeType !== DOCUMENT_NODE
    ) {
      const step = ignoreClass
        ? this.filteredStep(currentNode, ignoreClass)
        : this.stepFromNode(currentNode);

      if (step) segment.steps.unshift(step);
      currentNode = currentNode.parentNode;
    }

    if (offset != null && offset >= 0) {
      segment.terminal.offset = this.mergedTextOffset(node, offset, ignoreClass);
      // Ensure we land on a text node
      if (
        segment.steps.length > 0 &&
        segment.steps[segment.steps.length - 1].type !== "text"
      ) {
        segment.steps.push({ type: "text", index: 0, id: null });
      }
    }

    return segment;
  }

  private stepFromNode(node: Node): Step {
    const nodeType = node.nodeType === TEXT_NODE ? "text" : "element";
    return {
      id: (node as Element).id || null,
      type: nodeType,
      index: this.position(node),
    };
  }

  private filteredStep(node: Node, ignoreClass: string): Step | undefined {
    const filteredNode = this.filter(node, ignoreClass);
    if (!filteredNode) return undefined;

    const nodeType = filteredNode.nodeType === TEXT_NODE ? "text" : "element";
    return {
      id: (filteredNode as Element).id || null,
      type: nodeType,
      index: this.filteredPosition(filteredNode, ignoreClass),
    };
  }

  private position(anchor: Node): number {
    if (anchor.nodeType === ELEMENT_NODE) {
      const children =
        (anchor.parentNode as Element)?.children ??
        findChildren(anchor.parentNode!);
      return Array.prototype.indexOf.call(children, anchor);
    } else {
      const children = anchor.parentNode!.childNodes;
      const map = normalizedMap(children, TEXT_NODE);
      const idx = Array.prototype.indexOf.call(children, anchor);
      return map[idx] ?? 0;
    }
  }

  private filteredPosition(anchor: Node, ignoreClass: string): number {
    let map: Record<number, number>;

    if (anchor.nodeType === ELEMENT_NODE) {
      const children = (anchor.parentNode as Element).children;
      map = normalizedMap(Array.from(children), ELEMENT_NODE, ignoreClass);
      const idx = Array.prototype.indexOf.call(children, anchor);
      return map[idx] ?? 0;
    } else {
      // If inside an ignored node, step up
      let target: Node = anchor;
      if ((anchor.parentNode as Element)?.classList?.contains(ignoreClass)) {
        target = anchor.parentNode!;
      }
      const children = target.parentNode!.childNodes;
      map = normalizedMap(children, TEXT_NODE, ignoreClass);
      const idx = Array.prototype.indexOf.call(children, target);
      return map[idx] ?? 0;
    }

    return 0;
  }

  private mergedTextOffset(
    anchor: Node,
    offset: number,
    ignoreClass?: string,
  ): number {
    const target = this.textSlotNode(anchor, ignoreClass);
    if (!target?.parentNode) return offset;

    const children = target.parentNode.childNodes;
    const map = normalizedMap(children, TEXT_NODE, ignoreClass);
    const targetIndex = Array.prototype.indexOf.call(children, target);
    const slotIndex = map[targetIndex];
    if (slotIndex == null) return offset;

    let adjustedOffset = offset;
    for (let i = 0; i < targetIndex; i++) {
      if (map[i] === slotIndex) {
        adjustedOffset += ignoredNodeTextLength(children[i], ignoreClass);
      }
    }

    return adjustedOffset;
  }

  private textSlotNode(anchor: Node, ignoreClass?: string): Node | null {
    if (
      anchor.nodeType === TEXT_NODE &&
      ignoreClass &&
      (anchor.parentNode as Element)?.classList?.contains(ignoreClass)
    ) {
      return anchor.parentNode;
    }

    if (anchor.nodeType === TEXT_NODE) return anchor;

    if (
      anchor.nodeType === ELEMENT_NODE &&
      ignoreClass &&
      (anchor as Element).classList?.contains(ignoreClass)
    ) {
      return anchor;
    }

    return null;
  }

  private filter(anchor: Node, ignoreClass: string): Node | false {
    let needsIgnoring: boolean;
    const isText = anchor.nodeType === TEXT_NODE;

    if (isText) {
      needsIgnoring =
        (anchor.parentNode as Element)?.classList?.contains(ignoreClass) ??
        false;
    } else {
      needsIgnoring =
        (anchor as Element).classList?.contains(ignoreClass) ?? false;
    }

    if (needsIgnoring && isText) {
      const parent = anchor.parentNode!;
      const prev = parent.previousSibling;
      const next = parent.nextSibling;
      if (prev && prev.nodeType === TEXT_NODE) return prev;
      if (next && next.nodeType === TEXT_NODE) return next;
      return anchor; // parent will be ignored on next step
    } else if (needsIgnoring && !isText) {
      return false; // skip element entirely
    }

    return anchor;
  }

  private findNode(
    steps: Step[],
    doc: Document,
    ignoreClass?: string,
  ): Node | null {
    return this.walkToNode(steps, doc, ignoreClass);
  }

  private walkToNode(
    steps: Step[],
    doc: Document,
    ignoreClass?: string,
  ): Node | null {
    let container: Node | null = doc.documentElement;

    for (const step of steps) {
      if (!container) break;
      if (step.type === "element") {
        // Skip past single ignored-class wrapper
        if (
          ignoreClass &&
          (container as Element).children?.length === 1 &&
          ((container as Element).children[0] as Element).classList?.contains(
            ignoreClass,
          )
        ) {
          container = (container as Element).firstElementChild;
        }

        const elChildren: ArrayLike<Element> =
          (container as Element).children ?? findChildren(container!);
        const indexedChild = elChildren[step.index] ?? null;

        if (step.id) {
          const assertedChild =
            Array.prototype.find.call(
              elChildren,
              (child: Element) => child.id === step.id,
            ) ?? null;
          container =
            indexedChild?.id === step.id ? indexedChild : assertedChild;
        } else {
          container = indexedChild;
        }
      } else if (step.type === "text") {
        const children: NodeListOf<ChildNode> = container.childNodes;
        const map = normalizedMap(children, TEXT_NODE, ignoreClass);
        let nextContainer: Node | null = null;
        for (const childIndex in map) {
          if (!Object.prototype.hasOwnProperty.call(map, childIndex)) continue;
          if (map[childIndex] === step.index) {
            const child: ChildNode = children[parseInt(childIndex, 10)];
            if (!isZeroLengthIgnoredNode(child, ignoreClass)) {
              nextContainer =
                child.nodeType === ELEMENT_NODE && child.firstChild
                  ? child.firstChild
                  : child;
              break;
            }
          }
        }
        container =
          nextContainer ?? textNodes(container, ignoreClass)[step.index] ?? null;
      }
    }

    return container;
  }

  private fixMiss(
    steps: Step[],
    offset: number,
    doc: Document,
    ignoreClass?: string,
  ): { container: Node; offset: number } {
    const container = this.findNode(steps.slice(0, -1), doc, ignoreClass);
    if (!container) return { container: doc.documentElement, offset: 0 };

    const children = container.childNodes;
    const map = normalizedMap(children, TEXT_NODE, ignoreClass);
    const lastStepIndex = steps[steps.length - 1].index;
    let resultContainer: Node = container;
    let resultOffset = offset;

    for (const childIndex in map) {
      if (!Object.prototype.hasOwnProperty.call(map, childIndex)) continue;

      if (map[childIndex] === lastStepIndex) {
        const child = children[parseInt(childIndex, 10)];
        if (isZeroLengthIgnoredNode(child, ignoreClass)) continue;

        const len = ignoredNodeTextLength(child, ignoreClass);
        if (resultOffset > len) {
          resultOffset -= len;
        } else {
          resultContainer =
            child.nodeType === ELEMENT_NODE && child.firstChild
              ? child.firstChild
              : child;
          break;
        }
      }
    }

    return { container: resultContainer, offset: resultOffset };
  }
  private equalStep(a: Step | undefined, b: Step | undefined): boolean {
    if (!a || !b) return false;
    return a.index === b.index && a.id === b.id && a.type === b.type;
  }
}

export default EpubCFI;
